import { Fragment } from 'react';
import { z } from 'zod';

import { modelProperty } from '~/lib/api/models';
import { cn } from '~/lib/utils/classname';

import Heading from '../atoms/Heading';

export type DiagnosticType = 'dpe' | 'ges';

export interface Props {
  type: DiagnosticType;
  data: z.infer<typeof modelProperty.schema>['property']['propertyMeta'];
}

export const diagnosticLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G'] as const;

export const diagnosticPalettes: Record<
  DiagnosticType,
  Record<(typeof diagnosticLetters)[number], string>
> = {
  dpe: {
    A: 'text-[#007955]',
    B: 'text-[#4e854e]',
    C: 'text-[#779047]',
    D: 'text-[#ffba00]',
    E: 'text-[#f98e03]',
    F: 'text-[#ec3f09]',
    G: 'text-[#e6000a]',
  },

  ges: {
    A: 'text-violet-300',
    B: 'text-violet-400',
    C: 'text-violet-500',
    D: 'text-violet-600',
    E: 'text-violet-700',
    F: 'text-violet-800',
    G: 'text-violet-900',
  },
};

const DiagnosticsGraph: React.FC<Props> = ({ type, data }) => {
  const activeLetterProp: keyof typeof data =
    type === 'dpe' ? 'diagnosticDpeLetter' : 'diagnosticGesLetter';

  const activeLetter = data[
    activeLetterProp
  ]?.toLocaleUpperCase() as (typeof diagnosticLetters)[number];

  return (
    <div className="relative grid grid-cols-[3fr_2fr]">
      <div
        className="grid grow items-center gap-x-2 gap-y-1"
        style={{
          gridTemplateColumns: `auto minmax(0, 3fr) repeat(${diagnosticLetters.length - 1}, minmax(0, 1fr)) 40px`,
        }}
      >
        <div
          className="text-brand-quinary relative row-start-1 text-xs"
          style={{ gridColumn: `2 / span ${diagnosticLetters.length}` }}
        >
          {type === 'dpe'
            ? 'Logement très performant'
            : 'Peu d’émissions de CO₂'}
        </div>

        {diagnosticLetters.map((letter, index) => (
          <Fragment key={letter}>
            <span
              className="col-start-1 font-semibold"
              style={{ gridRow: index + 2 }}
            >
              {letter}
            </span>

            <div
              className={cn(
                'relative flex h-5 w-full items-center rounded-l-sm bg-current px-2 text-xs drop-shadow-[8px_0_0_theme("colors.white")]',
                diagnosticPalettes[type][letter],
                {
                  'w-[calc(100%-8px)]': type === 'dpe',
                  'rounded-r-xl': type === 'ges',
                }
              )}
              style={{
                gridRow: index + 2,
                gridColumn: `2 / span ${index + 1}`,
              }}
            >
              {type === 'dpe' && ['F', 'G'].includes(letter) && (
                <span className="text-white">Passoire énergétique</span>
              )}

              {type === 'dpe' && (
                <div
                  className="border-y-full pointer-events-none absolute top-0 left-full h-0 w-0 border-x-8 border-y-[10px] border-transparent border-l-current"
                  aria-hidden
                />
              )}
            </div>
          </Fragment>
        ))}

        {activeLetter && (
          <div
            className={cn(
              'pointer-events-none left-0 col-start-2 h-px w-full bg-current/75',
              diagnosticPalettes[type][activeLetter]
            )}
            style={{
              gridRow: Math.max(0, diagnosticLetters.indexOf(activeLetter)) + 2,
              gridColumn: `2 / span ${diagnosticLetters.length + 1}`,
            }}
          />
        )}

        <div
          className="text-brand-quinary relative text-xs"
          style={{ gridColumn: `2 / span ${diagnosticLetters.length}` }}
        >
          {type === 'dpe'
            ? 'Logement extrêmement consommateur d’énergie'
            : 'Émissions de CO₂ très importantes'}
        </div>
      </div>

      <aside
        className={cn(
          'rounded-lg border border-current/75 bg-current/10 px-4 py-3',
          diagnosticPalettes[type][activeLetter] || 'text-brand-quinary'
        )}
      >
        <div
          className={cn('relative flex flex-col gap-y-3 text-sm', {
            'text-brand-primary': activeLetter,
            'text-brand-quinary': !activeLetter,
          })}
        >
          <Heading
            as="h4"
            level={activeLetter ? 3 : 4}
            className={cn('font-title font-medium', {
              'text-brand-quinary': !activeLetter,
            })}
          >
            {activeLetter || `Classe ${type.toLocaleUpperCase()} inconnue`}
          </Heading>

          {type === 'dpe' && data.diagnosticDpeValue && (
            <div>
              <p>Consommation (énergie primaire)</p>
              <strong>
                {data.diagnosticDpeValue} kWh/m<sup>2</sup>.an
              </strong>
            </div>
          )}

          {data.diagnosticGesValue && (
            <div>
              <p>Émissions</p>
              <strong>
                {data.diagnosticGesValue} kg CO<sub>2</sub>/m<sup>2</sup>.an
              </strong>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};

export default DiagnosticsGraph;
